import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SetupPanel } from "@/components/setup-panel";
import { SampleBanner } from "@/components/sample-banner";
import { SearchInput } from "@/components/search-input";
import { MetricToggleChart, type ToggleMetric } from "@/components/metric-chart";
import { ChartCard } from "@/components/charts/chart-card";
import { HighlightRow, type HighlightItem } from "@/components/charts/highlight-row";
import { SplitCard, type SplitMetric } from "@/components/charts/split-card";
import { CompareNote } from "@/components/analytics/report-shell";
import { AdsProductsTable } from "@/components/ads-products-table";
import { SkHeader, SkMetricChart, SkTiles } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { CampaignsTab, SearchTermsTab, WhenWhereTab } from "@/features/google-ads/tabs";
import { DEFAULT_PRESET } from "@shared/analytics-range";
import { enumerateDays } from "@shared/date-range";
import { backPerDollar, pctChange } from "@shared/analytics";
import { campaignTypeLabel } from "@shared/ads-labels";
import { fmtNum, fmtReturn, fmtUSD } from "@shared/format";
import type { AgencyAdsDaily } from "@shared/agency-types";
import type { GoogleAdsPage } from "@shared/pages/google-ads";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

// The products search (?q=) filters in the browser from the report the page
// already holds, so it stays out of the page query's key: typing never
// refetches the report.
function withoutQuery(search: Record<string, string | undefined>): Record<string, string | undefined> {
  const rest = { ...search };
  delete rest.q;
  return rest;
}

export const Route = createFileRoute("/_app/google-ads")({
  loaderDeps: ({ search }) => withoutQuery(search),
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<GoogleAdsPage>("google-ads", deps));
  },
  component: GoogleAdsRoute,
});

const SECTIONS: Record<string, { title: string; info?: string }> = {
  performance: { title: "Google Ads" },
  campaigns: { title: "Campaigns" },
  products: { title: "Products in Google Ads" },
  "search-terms": { title: "Search terms", info: "What people searched before they clicked an ad." },
  "when-where": { title: "When & where", info: "The days, hours, places and devices where the ads work best." },
};

const dayWithWeekday = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" });

function Skeleton() {
  return (
    <div className="page-shell">
      <SkHeader />
      <div className="mt-5 space-y-4">
        <SkMetricChart />
        <SkTiles count={4} />
      </div>
    </div>
  );
}

function GoogleAdsRoute() {
  useTitle("Google Ads");
  const search = Route.useSearch();
  const q = (search.q ?? "").trim().toLowerCase();
  return (
    <PageQuery<GoogleAdsPage> name="google-ads" search={withoutQuery(search)} skeleton={<Skeleton />}>
      {(page) => <GoogleAds page={page} q={q} />}
    </PageQuery>
  );
}

function GoogleAds({ page, q }: { page: GoogleAdsPage; q: string }) {
  const { range: r, report, prev } = page;

  const headerFor = (key: string) => {
    const section = SECTIONS[key] ?? SECTIONS.performance;
    return <PageHeader title={section.title} info={section.info} actions={<DateRangeFilter presets={r.presets} defaultKey={DEFAULT_PRESET} compare />} />;
  };
  const header = headerFor(page.tab);

  if (!report) {
    return (
      <div className="page-shell">
        {header}
        {page.source === "unavailable" ? (
          <SetupPanel title="Google Ads numbers aren't available right now">
            Google Ads numbers didn&apos;t come through for this range. Try again in a few minutes; if it keeps happening, ask
            Digital King to check the connection.
          </SetupPanel>
        ) : (
          <SetupPanel title="Google Ads isn't connected yet">
            Spend, clicks and Google-reported sales show here once Digital King connects the Google Ads account.
          </SetupPanel>
        )}
      </div>
    );
  }

  const t = report.totals;
  const pt = prev?.totals ?? null;
  const campaigns = report.campaigns ?? [];
  const products = report.products ?? [];
  // The products search (?q=): title or Google's item id, case-insensitive.
  const shownProducts = q ? products.filter((p) => (p.title || "").toLowerCase().includes(q) || p.itemId.toLowerCase().includes(q)) : products;
  const tab = page.tab;

  const hasCost = t.cost !== undefined;
  const hasOrders = t.conversions !== undefined;
  const hasValue = t.conversionValue !== undefined;
  const back = backPerDollar(t.conversionValue, t.cost) ?? t.roas ?? null;
  const prevBack = pt ? backPerDollar(pt.conversionValue, pt.cost) : null;
  const delta = (cur: number | null | undefined, before: number | null | undefined) =>
    pt && cur !== null && cur !== undefined ? pctChange(cur, before) : null;

  // Performance: one toggle card per metric the hub sends, each against the previous window.
  const days = enumerateDays({ from: r.from, to: r.to });
  const byDate = new Map(report.daily.map((d) => [d.date, d]));
  const series = (f: (d: AgencyAdsDaily) => number) =>
    days.map(({ date }) => {
      const d = byDate.get(date);
      return d ? f(d) : 0;
    });
  const metrics: ToggleMetric[] = [
    ...(hasCost
      ? [{ key: "spend", label: "Spend", color: "#a86f5c", axis: "hidden", format: "usd", value: fmtUSD(t.cost), points: series((d) => d.cost ?? 0), defaultOn: true, delta: delta(t.cost, pt?.cost), invert: true } satisfies ToggleMetric]
      : []),
    ...(hasValue
      ? [{ key: "sales", label: "Google-reported sales", color: "var(--primary)", axis: "hidden", format: "usd", value: fmtUSD(t.conversionValue), points: series((d) => d.conversionValue ?? 0), defaultOn: true, delta: delta(t.conversionValue, pt?.conversionValue) } satisfies ToggleMetric]
      : []),
    ...(hasCost && hasValue
      ? [{ key: "back", label: "Back per $1", color: "#5b7fa6", axis: "hidden", format: "usd2", value: fmtReturn(back), points: series((d) => ((d.cost ?? 0) > 0 ? (d.conversionValue ?? 0) / d.cost! : 0)), delta: pt ? pctChange(back ?? 0, prevBack) : null } satisfies ToggleMetric]
      : []),
    ...(hasOrders
      ? [{ key: "orders", label: "Orders (Google)", color: "#7c8b5e", axis: "hidden", format: "number", value: fmtNum(t.conversions), points: series((d) => d.conversions ?? 0), delta: delta(t.conversions, pt?.conversions) } satisfies ToggleMetric]
      : []),
    { key: "clicks", label: "Clicks", color: "#8e7cc3", axis: "left", format: "number", value: fmtNum(t.clicks), points: series((d) => d.clicks), defaultOn: !hasCost, delta: delta(t.clicks, pt?.clicks) },
    { key: "impressions", label: "Times shown", color: "#6f8f8a", axis: "right", format: "number", value: fmtNum(t.impressions), points: series((d) => d.impressions), delta: delta(t.impressions, pt?.impressions) },
  ];

  const biggest = [...campaigns].sort((a, b) => (hasCost ? (b.cost ?? 0) - (a.cost ?? 0) : b.clicks - a.clicks))[0];
  const dayScore = (d: AgencyAdsDaily) => (hasValue ? d.conversionValue ?? 0 : hasOrders ? d.conversions ?? 0 : d.clicks);
  const bestDay = [...report.daily].sort((a, b) => dayScore(b) - dayScore(a))[0];
  const highlights: HighlightItem[] = [
    ...(biggest
      ? [{
          label: "Biggest campaign",
          value: biggest.name,
          sub: hasCost
            ? `${fmtUSD(biggest.cost)} spent${backPerDollar(biggest.conversionValue, biggest.cost) !== null ? ` · ${fmtReturn(backPerDollar(biggest.conversionValue, biggest.cost))} back per $1` : ""}`
            : `${fmtNum(biggest.clicks)} clicks`,
        }]
      : []),
    ...(bestDay && dayScore(bestDay) > 0
      ? [{
          label: "Best day",
          value: dayWithWeekday(bestDay.date),
          sub: hasValue
            ? `${fmtUSD(bestDay.conversionValue)} Google-reported sales`
            : hasOrders
              ? `${fmtNum(bestDay.conversions)} orders (Google)`
              : `${fmtNum(bestDay.clicks)} clicks`,
        }]
      : []),
    ...(hasCost && hasOrders && (t.conversions ?? 0) > 0
      ? [{ label: "Cost per order", value: fmtUSD(t.cost! / t.conversions!, true), sub: `${fmtNum(t.conversions)} orders (Google)` }]
      : []),
    ...(hasCost && t.clicks > 0 ? [{ label: "Cost per click", value: fmtUSD(t.cost! / t.clicks, true), sub: `${fmtNum(t.clicks)} clicks` }] : []),
  ];

  // Donuts: where the money, the sales and the clicks went, by campaign and by campaign type.
  type SliceRow = { key: string; label: string; cost?: number; conversionValue?: number; clicks: number };
  const campaignRows: SliceRow[] = campaigns.map((c, i) => ({ key: c.id ?? `${c.name}-${i}`, label: c.name, cost: c.cost, conversionValue: c.conversionValue, clicks: c.clicks }));
  const typeMap = new Map<string, SliceRow>();
  for (const c of campaigns) {
    const k = (c.channelType ?? "other").toLowerCase();
    const row = typeMap.get(k) ?? { key: k, label: campaignTypeLabel(c.channelType), cost: hasCost ? 0 : undefined, conversionValue: hasValue ? 0 : undefined, clicks: 0 };
    if (hasCost) row.cost = (row.cost ?? 0) + (c.cost ?? 0);
    if (hasValue) row.conversionValue = (row.conversionValue ?? 0) + (c.conversionValue ?? 0);
    row.clicks += c.clicks;
    typeMap.set(k, row);
  }
  const splitMetrics = (rows: SliceRow[]): SplitMetric[] => [
    ...(hasCost ? [{ key: "spend", label: "Spend", format: "usd" as const, centerLabel: "Ad spend", slices: rows.map((x) => ({ key: x.key, label: x.label, value: x.cost ?? 0 })) }] : []),
    ...(hasValue ? [{ key: "sales", label: "Sales", format: "usd" as const, centerLabel: "Google-reported sales", slices: rows.map((x) => ({ key: x.key, label: x.label, value: x.conversionValue ?? 0 })) }] : []),
    { key: "clicks", label: "Clicks", format: "number" as const, centerLabel: "Clicks", slices: rows.map((x) => ({ key: x.key, label: x.label, value: x.clicks })) },
  ];

  return (
    <div className="page-shell">
      {headerFor(tab)}
      {page.source === "sample" && <SampleBanner what="Google Ads numbers" source="Google Ads" />}

      {tab === "performance" && (
        <div className="mt-5 space-y-4">
          <div>
            <MetricToggleChart labels={days.map((d) => d.label)} metrics={metrics} />
            <CompareNote range={{ prev: prev ? r.prev : null, noCompare: r.noCompare }} />
          </div>
          <HighlightRow items={highlights} />
          {campaigns.length > 0 && (
            <div className="grid gap-4 @3xl:grid-cols-2">
              <SplitCard title="By campaign" hint="Campaigns that ran in this range. From Google Ads." metrics={splitMetrics(campaignRows)} />
              <SplitCard title="By campaign type" hint="From Google Ads." metrics={splitMetrics([...typeMap.values()])} />
            </div>
          )}
        </div>
      )}

      {tab === "campaigns" && <CampaignsTab campaigns={campaigns} />}

      {tab === "products" && (
        <ChartCard
          className="mt-5"
          title={q ? `${fmtNum(shownProducts.length)} of ${fmtNum(products.length)} products` : "Products in Google Ads"}
          hint="From Google Ads. Google credits each sale to the product whose ad was clicked."
          right={
            <div className="w-full @md:w-72">
              <SearchInput placeholder={"Find a product…"} />
            </div>
          }
        >
          <AdsProductsTable
            products={shownProducts}
            emptyText={q ? "No product in Google Ads matches that search." : undefined}
            productHref={(itemId) => page.productLinks[itemId]?.href}
            productImage={(itemId) => page.productLinks[itemId]?.imageUrl}
          />
        </ChartCard>
      )}

      {tab === "search-terms" && <SearchTermsTab report={report} />}

      {tab === "when-where" && <WhenWhereTab report={report} />}
    </div>
  );
}
